import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface AssumedUser {
  id: number
  github_id: number
  github_username: string
}

interface RoleAssumptionContextType {
  assumedUser: AssumedUser | null
  isRoleAssumed: boolean
  assumeRole: (user: AssumedUser) => void
  releaseRole: () => void
}

const RoleAssumptionContext = createContext<RoleAssumptionContextType | undefined>(undefined)

export const useRoleAssumption = () => {
  const context = useContext(RoleAssumptionContext)
  if (context === undefined) {
    throw new Error('useRoleAssumption must be used within a RoleAssumptionProvider')
  }
  return context
}

interface RoleAssumptionProviderProps {
  children: ReactNode
}

const STORAGE_KEY = 'assumed_role'

export const RoleAssumptionProvider: React.FC<RoleAssumptionProviderProps> = ({ children }) => {
  const [assumedUser, setAssumedUser] = useState<AssumedUser | null>(null)

  // Load assumed role from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const user = JSON.parse(stored)
        setAssumedUser(user)
      } catch (e) {
        console.error('Failed to parse assumed role from localStorage:', e)
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [])

  const assumeRole = (user: AssumedUser) => {
    setAssumedUser(user)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user))
  }

  const releaseRole = () => {
    setAssumedUser(null)
    localStorage.removeItem(STORAGE_KEY)
  }

  const value: RoleAssumptionContextType = {
    assumedUser,
    isRoleAssumed: !!assumedUser,
    assumeRole,
    releaseRole,
  }

  return (
    <RoleAssumptionContext.Provider value={value}>
      {children}
    </RoleAssumptionContext.Provider>
  )
}

